import { Sorter } from '../../components/Sorter/Sorter'
import { SortOrder } from 'antd/es/table/interface'

import { ColumnsType } from 'antd/es/table'
import { Link } from '../../components/Link'
import { NumberCell } from '../../components/Table/Cells/NumberCell/NumberCell'
import {
  ChangeLineDataFunc,
  SelectItemToShowHistoryFunc,
  TCommunitySavingGoals,
  THSSavingGoalsTable
} from './types'
import { ReactComponent as More16 } from '../../assets/svg/More16.svg'
import { Button, BUTTON_TYPES } from '../../components/Button'
import { BTN_TXT } from '../../constants'
import { currencyFormatterTwoDecimals } from '../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { routes } from '../../router'

export const communityColumns = ({
  isEditable,
  setCommunitySavingGoal,
  selectItemToShowHistory
}: {
  isEditable?: boolean
  setCommunitySavingGoal: (value: number) => void
  selectItemToShowHistory: SelectItemToShowHistoryFunc
}): ColumnsType<TCommunitySavingGoals> => [
  {
    dataIndex: 'name',
    key: 'name',
    width: '50%',
    title: 'Community name',
    render: (_, { name }) => (
      <Link to={`${routes.myCommunity}${routes.myOrganization}/general`}>
        {name}
      </Link>
    )
  },
  {
    dataIndex: 'savings_goal',
    key: 'savings_goal',
    width: '50%',
    title: 'Savings goal, $',
    render: (_, { savings_goal }) => (
      <NumberCell
        alwaysEditable={isEditable}
        value={String(savings_goal)}
        onChange={(val) => {
          setCommunitySavingGoal(Number(val))
        }}
        isCurrency
        placeholder={'-'}
        inputProps={{
          min: '0',
          max: '9999999999.99',
          maxLength: 15,
          formatter: currencyFormatterTwoDecimals
        }}
      />
    ),
    onCell: ({ changed }) => ({
      className: changed ? 'cell-changed' : ''
    })
  },
  {
    dataIndex: 'actions',
    key: 'actions',
    className: 'vertical-align-middle p-0',
    title: '',
    width: 24,
    fixed: 'right',
    render: (_, { uuid, name, has_history_records }) =>
      has_history_records ? (
        <Button.Menu
          icon={<More16 />}
          type={BUTTON_TYPES.GHOST}
          options={[
            {
              text: BTN_TXT.HISTORY,
              onClick: () =>
                selectItemToShowHistory({
                  uuid,
                  name: name,
                  type: 'community'
                })
            }
          ]}
          iconOnly
          hideArrow
        />
      ) : null
  }
]

export const hsColumns = (
  { isEditableSavingGoal, disableActions, hideHistoryColumn },
  onChangeLineData: ChangeLineDataFunc,
  selectItemToShowHistory: SelectItemToShowHistoryFunc,
  highlightErrors?: boolean
): ColumnsType<THSSavingGoalsTable> => [
  {
    dataIndex: 'name',
    key: 'name',
    width: '50%',
    title: ({ sortOrder, sortColumn }) => (
      <div
        className="row full-width space-between align-center"
        style={{ height: 25 }}
      >
        <Sorter<THSSavingGoalsTable>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
          disabled={disableActions}
          disabledTooltipText={'Sorting is disabled in edit mode'}
        >
          Health system
        </Sorter>
      </div>
    ),
    sorter: !disableActions,
    showSorterTooltip: false,
    render: (_, { name, children, uuid }) => {
      if (children)
        return (
          <Link
            to={`${routes.myCommunity}${routes.myOrganization}/health_systems/${uuid}`}
          >
            {name}
          </Link>
        )
      else return <div className="hospital-level-name">{name}</div>
    }
  },
  {
    dataIndex: 'savings_goal',
    key: 'savings_goal',
    width: '50%',
    title: ({ sortOrder, sortColumn }) => (
      <div
        className="row full-width space-between align-center"
        style={{ height: 25 }}
      >
        <Sorter<THSSavingGoalsTable>
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
          disabled={disableActions}
          disabledTooltipText={'Sorting is disabled in edit mode'}
        >
          Savings goal, $
        </Sorter>
      </div>
    ),
    sorter: !disableActions,
    showSorterTooltip: false,
    render: (_, { savings_goal, error, uuid, hs_uuid, can_be_edited }) => {
      return (
        <NumberCell
          alwaysEditable={isEditableSavingGoal && can_be_edited}
          value={savings_goal}
          onChange={(val) => {
            onChangeLineData({ uuid, hs_uuid }, val)
          }}
          error={error}
          isCurrency
          placeholder={'-'}
          inputProps={{
            min: '0',
            max: '9999999999.99',
            maxLength: 15,
            formatter: currencyFormatterTwoDecimals
          }}
        />
      )
    },
    onCell: ({ error, changed }) => ({
      className:
        error && highlightErrors
          ? 'cell-with-error'
          : changed
          ? 'cell-changed'
          : ''
    })
  },
  {
    dataIndex: 'actions',
    key: 'actions',
    className: 'vertical-align-middle p-0',
    title: '',
    width: 24,
    fixed: 'right',
    render: (_, { uuid, hs_uuid, name, has_history_records, can_be_edited }) =>
      has_history_records && can_be_edited && !hideHistoryColumn ? (
        <Button.Menu
          icon={<More16 />}
          type={BUTTON_TYPES.GHOST}
          options={[
            {
              text: BTN_TXT.HISTORY,
              onClick: () =>
                selectItemToShowHistory({
                  name,
                  uuid,
                  type: hs_uuid ? 'hospital' : 'health_system'
                })
            }
          ]}
          iconOnly
          hideArrow
        />
      ) : null
  }
]
